import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { Config } from "../config";
import moment from "moment-timezone";

const baseUrl = Config.serviceUrls.schedulerBaseUrl;
const OFFICE_POINT_URL = Config.serviceUrls.officeBaseUrl;
const notesBaseUrl = Config.serviceUrls.notesBaseUrl;
const utilityBaseUrl = Config.serviceUrls.utilityBaseUrl;
const userBaseUrl = Config.serviceUrls.userBaseUrl;

export async function createSchedulerEvent(data) {
  const response = await axios.post(`${baseUrl}/SchedulerEvent`, data);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response;
}
export function useCreateSchedulerEventMutation() {
  return useMutation((data) => createSchedulerEvent(data), {});
}

export async function createSchedulerEventCheckingAvailability(data) {
  const response = await axios.post(
    `${baseUrl}/Scheduler/ConflictingScheduler`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}
export function useCreateSchedulerEventCheckingAvailabilityMutation() {
  return useMutation(
    (data) => createSchedulerEventCheckingAvailability(data),
    {},
  );
}

export async function closeShift(data) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/ShiftClose/${data.id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}
export function useCloseShift() {
  return useMutation((data) => closeShift(data), {});
}

export async function updateSchedulerEvent(data) {
  const response = await axios.put(`${baseUrl}/SchedulerEvent`, data);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data?.message;
}
export function useCreateUpdateSchedulerEventMutation() {
  return useMutation((data) => updateSchedulerEvent(data), {});
}

export async function getAllActiveOffices(
  pageNumber,
  pageSize,
  ownerId,
  searchValue = "",
) {
  const objectCheck = ownerId && typeof ownerId === "object";
  const response = await axios.get(`${OFFICE_POINT_URL}/Office/AllActive`, {
    params: {
      pageNumber,
      pageSize,
      ownerId: objectCheck ? ownerId?.ownerId : ownerId,
      SearchTerm: searchValue,
      includeAdminOffice:
        objectCheck && ownerId?.includeAdminOffice
          ? ownerId.includeAdminOffice
          : false,
      IncludeOwnerOffices:
        objectCheck && ownerId?.includeOwnerOffices
          ? ownerId.includeOwnerOffices
          : false,
    },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useAllActiveOffices(
  pageNumber,
  pageSize,
  ownerId,
  searchValue = "",
  options = {},
) {
  const ownerIdParams = ownerId && typeof ownerId === "object" ? ownerId : null;
  return useQuery(
    [
      "/Office/AllActive",
      pageNumber,
      pageSize,
      ownerIdParams?.ownerId ?? ownerId,
      ownerIdParams?.includeAdminOffice ?? false,
      ownerIdParams?.includeOwnerOffices ?? false,
      searchValue,
    ],
    () => getAllActiveOffices(pageNumber, pageSize, ownerId, searchValue),
    options,
  );
}

export async function getStaffMembers(officeId) {
  const response = await axios.get(`${notesBaseUrl}/GlobalNote/OfficeMembers`, {
    params: { officeId },
  });
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export const getStaffMembersList = async (ownerId, officeId) => {
  const response = await axios.post(`${userBaseUrl}/User/StaffListByOwner`, {
    ownerId: ownerId,
    officeIds: officeId,
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};

export async function getStaffDesignation({ selectedId }) {
  const response = await axios.get(
    `${utilityBaseUrl}/Designation/getAllRolesDesignation${
      selectedId ? "?ownerId=" + selectedId : ""
    }`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useStaffDesignation(otherValues = {}, options = {}) {
  return useQuery(
    ["/Designation/getAllRolesDesignation", otherValues?.selectedId],
    () => getStaffDesignation(otherValues),
    options,
  );
}

export async function getOwnerSpecificStaffDesignation({ selectedId }) {
  const response = await axios.get(
    `${utilityBaseUrl}/Utility/DesignationByOwnerId${
      selectedId ? "?ownerId=" + selectedId : ""
    }`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useOwnerSpecificStaffDesignation(
  otherValues = {},
  options = {},
) {
  return useQuery(
    ["/Utility/DesignationByOwnerId", otherValues?.selectedId],
    () => getOwnerSpecificStaffDesignation(otherValues),
    options,
  );
}

export async function getAccountOwners() {
  const response = await axios.get(`${userBaseUrl}/Staff/OwnerList`);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useAccountOwners(options = {}) {
  return useQuery(["/Staff/OwnerList"], () => getAccountOwners(), options);
}

export async function setAccountOwnerSelected(data) {
  const response = await axios.patch(
    `${userBaseUrl}/User/UpdateDefaultOwner`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useUpdateAccountOwnerSelected() {
  return useMutation((data) => setAccountOwnerSelected(data), {});
}

export async function getEventListByAssignedUser(
  pageNumber,
  pageSize,
  ownerId,
  startDate,
  endDate,
  officeIds,
  statuses,
) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/ListByAssignedUser`,
    {
      pageNumber,
      pageSize,
      ownerId,
      startDate,
      endDate,
      officeIds,
      statuses,
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export function useEventListByAssignedUser(
  pageNumber,
  pageSize,
  ownerId,
  startDate,
  endDate,
  officeIds,
  statuses,
) {
  return useQuery(
    [
      "/SchedulerEvent/ListByAssignedUser",
      pageNumber,
      pageSize,
      ownerId,
      startDate,
      endDate,
      officeIds,
      statuses,
    ],
    () =>
      getEventListByAssignedUser(
        pageNumber,
        pageSize,
        ownerId,
        startDate,
        endDate,
        officeIds,
        statuses,
      ),
  );
}

export async function getSchedularNotes(startDate, endDate, ownerId) {
  const response = await axios.get(
    `${baseUrl}/Scheduler/DayNote/List?StartDate=${startDate}&EndDate=${endDate}&OwnerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function getOfficeNotesMonthly({ queryKey }) {
  const [, ownerId, startDate, endDate] = queryKey;
  const axiosRes = await axios.get(
    `${baseUrl}/Scheduler/DayNote/List?StartDate=${startDate}&EndDate=${endDate}&OwnerId=${ownerId}`,
  );
  const apiRes = axiosRes.data;
  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export function useGetNotesEventMonthly(
  ownerId,
  startDate,
  endDate,
  options = {},
) {
  return useQuery(
    ["MONTHLY_NOTES_EVENT", ownerId, startDate, endDate],
    getOfficeNotesMonthly,
    options,
  );
}

export async function addSchedularNotes(data) {
  const response = await axios.post(`${baseUrl}/Scheduler/DayNote`, data);

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function editSchedularNotes(text, noteId) {
  const response = await axios.put(
    `${baseUrl}/Scheduler/DayNote?Text=${text}&SchedulerDayNoteId=${noteId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function deleteSchedularNote(id) {
  const response = await axios.delete(
    `${baseUrl}/Scheduler/DayNote?SchedulerDayNoteId=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function checkOverlapEvent(schedulerEventId, OwnerId) {
  const response = await axios.get(
    `${baseUrl}/SchedulerEvent/CheckEventOverlap?SchedulerEventId=${schedulerEventId}&OwnerId=${OwnerId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function acceptEventRequest(schedulerEventId, officeId) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/Accept?SchedulerEventId=${schedulerEventId}&OfficeId=${officeId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export async function rejectEventRequest(
  schedulerEventId,
  officeId,
  ReasonForRejection,
) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/Reject?SchedulerEventId=${schedulerEventId}${
      officeId ? "&OfficeId=" + officeId : ""
    }&ReasonForRejection=${ReasonForRejection}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export async function cancelEventRequest(
  schedulerEventId,
  officeId,
  ReasonForCancel,
) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/Cancel?SchedulerEventId=${schedulerEventId}${
      officeId ? "&OfficeId=" + officeId : ""
    }&ReasonForCancel=${ReasonForCancel}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export async function getEventListForAssigner(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/ListForAssigner`,
    {
      ownerId,
      startDate,
      endDate,
      officeIds,
      userIds,
    },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getEventListForAssignerByEmployee(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
) {
  if (!ownerId || !startDate || !endDate) {
    return { statusCode: 200, data: [] };
  }

  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/ListForAssignerByEmployee`,
    {
      OwnerId: ownerId,
      StartDate: startDate,
      EndDate: endDate,
      OfficeIds: officeIds ?? [],
      userIds: userIds ?? [],
    },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function duplicateSchedulerEvent(payload) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/Duplicate`,
    payload,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getEventListDetails(
  SchedulerEventId,
  SchedulerEventDate = moment(new Date()).format("YYYY-MM-DD"),
) {
  const response = await axios.get(
    `${baseUrl}/SchedulerEvent/${SchedulerEventId}?SchedulerEventDate=${SchedulerEventDate}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function eventPublished(SchedulerEventId, PublishAsEvent) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/Publish?SchedulerEventId=${SchedulerEventId}&PublishAsEvent=${PublishAsEvent}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

/**
 * Bulk publish draft scheduler events/shifts (Event Workspace global publish).
 * @param {number[]} schedulerEventIds
 */
export async function publishSchedulerEvents(
  schedulerEventIds = [],
  publishStatus,
) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/PublishSchedulerEvents`,
    { SchedulerEventIds: schedulerEventIds, PublishStatus: publishStatus },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

export async function getEventPublishedList(
  pageNumber,
  pageSize,
  ownerId,
  statuses,
) {
  const response = await axios.post(`${baseUrl}/SchedulerEvent/PublishedList`, {
    pageNumber,
    pageSize,
    ownerId,
    statuses,
  });
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function requestToJoin(params) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/RequestToJoin`,
    params,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function acceptRequestEvents(id) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/RequestToJoin/Accept?EventRequestToJoinId=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function withDrawEvent(id) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/RequestToJoin/Withdraw?EventRequestToJoinId=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function declineEvents(id) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/RequestToJoin/Decline?SchedulerEventId=${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function deleteEvent(data) {
  const response = await axios.delete(`${baseUrl}/SchedulerEvent`, {
    data,
  });
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

/**
 * Bulk-delete selected scheduler event/shift occurrences (Event Workspace).
 * Each item deletes only the given occurrence date (not the full recurring series).
 * @param {{
 *   IsForAllStaff: boolean,
 *   Items: Array<{
 *     SchedulerEventId: number,
 *     DeletedDate: string,
 *     UserId: number|null,
 *   }>,
 * }} payload
 */
export async function deleteSchedulerEvents(payload) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/DeleteEvents`,
    payload,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

/**
 * Clear (delete) scheduler events/shifts in a date range for Event Workspace.
 * Deletes only occurrences within StartDate–EndDate (not entire recurring series).
 * @param {{
 *   OwnerId: number,
 *   StartDate: string,
 *   EndDate: string,
 *   IsShift: boolean|null,
 *   UserIds: number[],
 *   OfficeIds: number[],
 * }} payload
 * @returns {Promise<object>}
 */
export async function deleteSchedulerEventRange(payload) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/DeleteRange`,
    payload,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

/**
 * Copy scheduler events/shifts from a source date range into a target range
 * for Event Workspace (Copy Previous Day/Week/Month). Copies are created as
 * drafts; existing records in the target period are left unchanged.
 * @param {{
 *   OwnerId: number,
 *   SourceStartDate: string,
 *   SourceEndDate: string,
 *   TargetStartDate: string,
 *   TargetEndDate: string,
 *   IsShift: boolean|null,
 *   UserIds: number[],
 *   OfficeIds: number[],
 * }} payload
 * @returns {Promise<object>}
 */
export async function copySchedulerEventRange(payload) {
  const response = await axios.post(
    `${baseUrl}/SchedulerEvent/CopyRange`,
    payload,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

/**
 * Save the currently filtered Event Workspace period as a reusable template.
 * Includes only records matching the applied filters within SourceStartDate–SourceEndDate
 * (draft + published; recurring instances only within that range).
 * @param {{
 *   OwnerId: number,
 *   Name: string,
 *   ViewType: number, // TemplateViewType: Week=1, Month=2, Daily=3
 *   SourceStartDate: string,
 *   SourceEndDate: string,
 *   UserIds: number[],
 *   OfficeIds: number[],
 *   IsShift: boolean|null,
 * }} payload
 * @returns {Promise<object>}
 */
export async function saveSchedulerTemplate(payload) {
  const response = await axios.post(`${baseUrl}/SchedulerTemplate`, payload);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

/**
 * Lists saved scheduler templates for an owner (optionally filtered by name / view type).
 * @param {{
 *   OwnerId: number,
 *   ViewType?: number, // TemplateViewType: Week=1, Month=2, Daily=3
 *   NameSearch?: string,
 *   PageNumber?: number,
 *   PageSize?: number,
 * }} params
 * @returns {Promise<object>}
 */
export async function getAllSchedulerTemplates({
  OwnerId,
  ViewType,
  NameSearch,
  PageNumber = 1,
  PageSize = 50,
}) {
  const response = await axios.get(
    `${baseUrl}/SchedulerTemplate/GetAllTemplate`,
    {
      params: {
        OwnerId,
        ViewType,
        NameSearch: NameSearch || undefined,
        PageNumber,
        PageSize,
      },
    },
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

/**
 * React Query hook for GetAllTemplate.
 * @param {{
 *   ownerId: number|null|undefined,
 *   viewType?: number,
 *   nameSearch?: string,
 *   pageNumber?: number,
 *   pageSize?: number,
 *   enabled?: boolean,
 * }} options
 */
export function useGetAllSchedulerTemplates({
  ownerId,
  viewType,
  nameSearch = "",
  pageNumber = 1,
  pageSize = 50,
  enabled = true,
} = {}) {
  return useQuery(
    [
      "SchedulerTemplate/GetAllTemplate",
      ownerId,
      viewType,
      nameSearch,
      pageNumber,
      pageSize,
    ],
    () =>
      getAllSchedulerTemplates({
        OwnerId: ownerId,
        ViewType: viewType,
        NameSearch: nameSearch,
        PageNumber: pageNumber,
        PageSize: pageSize,
      }),
    {
      enabled: Boolean(enabled && ownerId),
      keepPreviousData: true,
    },
  );
}

/**
 * Applies a saved template into the target period as new draft records.
 * Existing shifts/events in the period are unchanged; template is not mutated.
 * @param {{
 *   OwnerId: number,
 *   TemplateId: number,
 *   TargetStartDate: string, // YYYY-MM-DD
 * }} payload
 * @returns {Promise<object>}
 */
export async function applySchedulerTemplate(payload) {
  const response = await axios.post(
    `${baseUrl}/SchedulerTemplate/Apply`,
    payload,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response?.data;
}

export async function deleteBusySlot(data) {
  const response = await axios.delete(`${baseUrl}/Scheduler`, {
    data,
  });
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function rejectRequestEventToJoin(id, reasonForReajection) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/RequestToJoin/Reject?EventRequestToJoinId=${id}&ReasonForRejection=${reasonForReajection}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function acceptRequestEventToJoin(EventRequestToJoinId) {
  const response = await axios.patch(
    `${baseUrl}/SchedulerEvent/RequestToJoin/Accept?EventRequestToJoinId=${EventRequestToJoinId}`,
  );

  if (response.data.statusCode !== 200 && response.data.statusCode !== 406) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function createBusySlot(body) {
  const response = await axios.post(`${baseUrl}/Scheduler/BusySlot`, body);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function updateBusySlot(body) {
  const response = await axios.put(`${baseUrl}/Scheduler/BusySlot`, body);
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}
export async function getAgenda({ queryKey }) {
  const [, OwnerId, StartDate, EndDate, OfficeIds] = queryKey;
  const body = { OwnerId, StartDate, EndDate, OfficeIds };
  const axiosRes = await axios.post(`${baseUrl}/Scheduler/Agenda`, body);
  const apiRes = axiosRes.data;
  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export function useGetAgenda(
  ownerId,
  startDate,
  endDate,
  officeIds,
  options = {},
) {
  const ids = [...officeIds].sort((a, b) => a - b);
  return useQuery(
    ["agenda", ownerId, startDate, endDate, ids],
    getAgenda,
    options,
  );
}

export async function getBusySlot({ queryKey }) {
  const [, busySlotId, SchedulerBusySlotDate] = queryKey;
  const axiosRes = await axios.get(
    `${baseUrl}/Scheduler/BusySlot/${busySlotId}?SchedulerBusySlotDate=${SchedulerBusySlotDate}`,
  );
  const apiRes = axiosRes.data;
  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export function useGetBusySlot(
  busySlotId,
  SchedulerBusySlotDate,
  options = {},
) {
  return useQuery(
    ["busy-slot-details", busySlotId, SchedulerBusySlotDate],
    getBusySlot,
    options,
  );
}

export async function deleteBusySlots(slotId) {
  const response = await axios.delete(
    `${baseUrl}/Scheduler/BusySlot/${slotId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data;
}

export async function getOfficeAgenda({ queryKey }) {
  const [, OwnerId, StartDate, EndDate, OfficeIds, UserIds] = queryKey;

  // Construct query parameters
  const params = new URLSearchParams({
    OwnerId,
    StartDate,
    EndDate,
  });

  // Append array parameters correctly
  if (OfficeIds?.length) {
    OfficeIds.forEach((id) => params.append("OfficeIds", id));
  }
  if (UserIds?.length) {
    UserIds.forEach((id) => params.append("UserIds", id));
  }

  // Make GET request with query parameters
  const axiosRes = await axios.get(
    `${baseUrl}/Scheduler/OfficeScheduler?${params.toString()}`,
  );

  const apiRes = axiosRes.data;

  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export async function getOfficeAgendaMonthly({ queryKey }) {
  const [, OwnerId, StartDate, EndDate, OfficeIds, UserIds] = queryKey;
  const body = { OwnerId, StartDate, EndDate, OfficeIds, UserIds };
  const axiosRes = await axios.post(
    `${baseUrl}/Scheduler/OfficeSchedulerMonthly`,
    body,
  );
  const apiRes = axiosRes.data;
  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}
// getOfficeEventMonthlygetOfficeEventMonthly

export async function getOfficeEventMonthly(
  ownerId,
  startDate,
  endDate,
  office_ids,
  user_ids,
) {
  const body = {
    OwnerId: ownerId,
    StartDate: startDate,
    EndDate: endDate,
    OfficeIds: office_ids,
    userIds: user_ids,
  };
  const axiosRes = await axios.post(
    `${baseUrl}/SchedulerEvent/ListForAssigner`,
    body,
  );
  const apiRes = axiosRes.data;
  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export async function getOfficeAgendaMonthlyList({ queryKey }) {
  const [, OwnerId, StartDate, EndDate, OfficeIds, UserIds] = queryKey;

  // Construct query parameters
  const params = new URLSearchParams({
    OwnerId,
    StartDate,
    EndDate,
  });

  // Add array parameters correctly
  if (OfficeIds?.length) {
    OfficeIds.forEach((id) => params.append("OfficeIds", id));
  }
  if (UserIds?.length) {
    UserIds.forEach((id) => params.append("UserIds", id));
  }

  // Make GET request with query parameters
  const axiosRes = await axios.get(
    `${baseUrl}/Scheduler/OfficeScheduler?${params.toString()}`,
  );

  const apiRes = axiosRes.data;

  if (apiRes.statusCode !== 200) {
    throw new Error(apiRes.message);
  }
  return apiRes;
}

export function useGetOfficeAgenda(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
  options = {},
) {
  const office_ids = [...officeIds].sort((a, b) => a - b);
  const user_ids = [...userIds].sort((a, b) => a - b);
  return useQuery(
    ["office_agenda", ownerId, startDate, endDate, office_ids, user_ids],
    getOfficeAgenda,
    options,
  );
}

export function useGetEventListForAssignerByEmployee(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
  options = {},
) {
  const office_ids = [...(officeIds || [])].sort((a, b) => a - b);
  const user_ids = [...(userIds || [])].sort((a, b) => a - b);
  return useQuery(
    [
      "event_list_assigner_by_employee",
      ownerId,
      startDate,
      endDate,
      office_ids,
      user_ids,
    ],
    () =>
      getEventListForAssignerByEmployee(
        ownerId,
        startDate,
        endDate,
        office_ids,
        user_ids,
      ),
    options,
  );
}

export function useGetOfficeAgendaMonthly(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
  options = {},
) {
  const office_ids = [...officeIds].sort((a, b) => a - b);
  const user_ids = [...userIds].sort((a, b) => a - b);
  return useQuery(
    [
      "office_agenda_Monthly",
      ownerId,
      startDate,
      endDate,
      office_ids,
      user_ids,
    ],
    getOfficeAgendaMonthly,
    options,
  );
}

export function useGetOfficeEventMonthly(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
  options = {},
) {
  const office_ids = [...officeIds].sort((a, b) => a - b);
  const user_ids = [...userIds].sort((a, b) => a - b);
  return useQuery(
    [
      "office_agenda_Monthly",
      ownerId,
      startDate,
      endDate,
      office_ids,
      user_ids,
    ],
    () =>
      getOfficeEventMonthly(ownerId, startDate, endDate, office_ids, user_ids),
    options,
  );
}

export function useGetOfficeAgendaMonthlyList(
  ownerId,
  startDate,
  endDate,
  officeIds,
  userIds,
  options = {},
) {
  const office_ids = [...officeIds].sort((a, b) => a - b);
  const user_ids = [...userIds].sort((a, b) => a - b);
  return useQuery(
    [
      "office_agenda_Monthly_List",
      ownerId,
      startDate,
      endDate,
      office_ids,
      user_ids,
    ],
    getOfficeAgendaMonthlyList,
    options,
  );
}

export async function getSyncCalendar(ownerId, startDate) {
  const response = await axios.get(`${baseUrl}/Scheduler/CalendarFileUrl`, {
    params: { ownerId, startDate },
  });

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getEventIcsLink(id) {
  const response = await axios.get(
    `${baseUrl}/Scheduler/IcsUrlForSingleEvent/${id}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export async function getBusySlotIcsLink(id) {
  const response = await axios.get(
    `${baseUrl}/Scheduler/IcsUrlForSingleBusySlot/${id}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export async function isEventDeclined(SchedulerEventId) {
  const response = await axios.get(
    `${baseUrl}/SchedulerEvent/${SchedulerEventId}/IsDeclined`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function getAssignedEmployess(
  SchedulerEventId,
  SchedulerEventDate = moment(new Date()).format("YYYY-MM-DD"),
  userIds,
) {
  const params = new URLSearchParams({
    SchedulerEventId,
    SchedulerEventDate,
  });

  if (userIds?.length) {
    userIds.forEach((id) => params.append("userIds", id));
  }

  const response = await axios.get(
    `${baseUrl}/SchedulerEvent/EventEmployeeDetail?${params.toString()}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function updateEventRequestDetail(data) {
  const response = await axios.put(
    `${baseUrl}/SchedulerEvent/UpdateRequestedEventReminderByStaff`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function updateEventReminderByStaff(data) {
  const response = await axios.put(
    `${baseUrl}/SchedulerEvent/UpdateEventReminderByStaff`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export async function getEventHistoryLogs(SchedulerEventId) {
  const response = await axios.get(
    `${baseUrl}/SchedulerEvent/SchedulerEventHistory?SchedulerEventId=${SchedulerEventId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export async function getBusySLotHistoryLogs(SchedulerBusySlotId) {
  const response = await axios.get(
    `${baseUrl}/Scheduler/SchedulerBusySlotHistory?SchedulerBusySlotId=${SchedulerBusySlotId}`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
}

export const getAvailableDatesList = async (params) => {
  const response = await axios.post(
    `${baseUrl}/Scheduler/SchedulerCommonFreeDate`,
    params,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data;
};
